<script setup lang="ts">
  import useTaboolaAds, {
    getAdBrandFromTile,
  } from '../../composables/ad-ops/useTaboolaAds'
  import {
    getIndexOfPlacement,
    calculateFeedPosition,
  } from '../../utils/ad-ops/adFunctions'

  /**
   * Props for the Taboola component
   * @property containerName - The name of the container for the ad
   * @property placement - The placement identifier for the ad
   * @property mode - style of the ad
   * @property tilePosition - Optional position of the tile in a list
   * @property adHeight - Optional height of the ad in pixels
   * @property adWidth - Optional width of the ad in pixels
   * @property  lazyRender - controls -visibilityThresholdInPercentage - Optional percentage of the element that must be visible
   * AND visibilityTimeoutInMs - Optional timeout in milliseconds before rendering when visible
   * @property adInstanceId - Optional unique identifier for the ad instance
   * @property sticky - Optional sticky class for the ad
   */
  export interface TaboolaProps {
    containerName: string
    placement: string
    mode: string
    tilePosition?: number
    isFeed?: boolean
    adInstanceId?: string
    lazyRender?: boolean
    sticky?: boolean
    adHeight?: string
    adWidth?: string
  }

  const props = withDefaults(defineProps<TaboolaProps>(), {
    tilePosition: -1,
    isFeed: false,
    adInstanceId: '',
    lazyRender: false,
    sticky: false,
    adHeight: 'auto',
    adWidth: 'auto',
  })

  const widgetWrapperEl = useTemplateRef<HTMLElement | null>('widgetWrapperEl')

  const { renderWhenCurrentElementIsVisible, adClicked } = useAdFunctions()

  const { triggerTaboolaRenderedEvent, fireRenderCallback } = useTaboolaAds()

  const { track } = useAnalytics()

  const containerName = ref<string>(props.containerName)
  const widgetName = ref<string>(props.placement)
  const placementClassName = ref<string>(props.placement)

  const { $assertive } = useNuxtApp()
  const taboolaExternId = $assertive?.analytics?.custom?.custom_4 || ''

  const adBrand = ref<string>('')
  const setAdBrand = (element: Element) => {
    adBrand.value = getAdBrandFromTile(element)
  }

  const renderTaboolaElement = ref(false)

  const getEventData = (): NtpAdEvent => ({
    ntp_ad_brand: adBrand.value,
    ntp_tile_position: props.tilePosition,
    ntp_ad_provider: 'taboola',
    ntp_creative_id: 'taboola-' + props.placement,
    ntp_placement_name: props.placement,
    ntp_extern_id: taboolaExternId,
  })

  /**
   * Capture-phase delegated click handler on the wrapper element.
   * Works for both feed layouts:
   *   - SN card layout: .tbl-feed-card[data-card-index] with .videoCube tiles
   *   - ON flat layout: .videoCube tiles directly in wrapper (no card grouping)
   * Also handles single-tile placements via fallback to props.tilePosition.
   */
  const handleDelegatedClick = (event: Event) => {
    const target = event.target
    if (!(target instanceof HTMLElement)) return

    const clickedAnchor = target.closest('a')
    if (!clickedAnchor) return

    const tileElement =
      clickedAnchor.closest('.videoCube') || clickedAnchor.closest('.tbl-item')
    if (!tileElement) return

    const tileBrand = getAdBrandFromTile(tileElement)

    // Calculate position based on DOM structure
    let tilePosition = props.tilePosition

    if (props.isFeed) {
      // Strategy 1: SN card layout (.tbl-feed-card[data-card-index])
      const feedCard = clickedAnchor.closest('.tbl-feed-card[data-card-index]')
      if (feedCard instanceof HTMLElement) {
        const cardIdxAttr = feedCard.getAttribute('data-card-index')
        const videoCube = clickedAnchor.closest('.videoCube')
        if (videoCube) {
          const allCubes = Array.from(feedCard.querySelectorAll('.videoCube'))
          const tileIdx = allCubes.indexOf(videoCube)
          const feedPosition = calculateFeedPosition(
            cardIdxAttr,
            tileIdx !== -1 ? String(tileIdx) : null
          )
          if (feedPosition !== null) {
            tilePosition = feedPosition
          }
        }
      } else if (widgetWrapperEl.value) {
        // Strategy 2: ON flat layout (no cards, all .videoCube under wrapper)
        const videoCube = clickedAnchor.closest('.videoCube')
        if (videoCube) {
          const allCubes = Array.from(
            widgetWrapperEl.value.querySelectorAll('.videoCube')
          )
          const tileIdx = allCubes.indexOf(videoCube)
          if (tileIdx !== -1) {
            const feedPosition = calculateFeedPosition('1', String(tileIdx))
            if (feedPosition !== null) {
              tilePosition = feedPosition
            }
          }
        }
      }
    }

    const clickEventData: NtpAdEvent = {
      ...getEventData(),
      ntp_ad_brand: tileBrand || adBrand.value,
      ntp_tile_position: tilePosition,
    }

    adClicked(clickEventData, props.placement)
  }

  /**
   * Attach the capture-phase click listener to the wrapper element.
   * Called immediately from handleTaboolaRenderedEvent — does NOT wait
   * for Taboola's render event, because capture-phase delegation works
   * even before feed cards exist in the DOM.
   */
  const attachClickTracking = () => {
    if (!widgetWrapperEl.value) return
    if (widgetWrapperEl.value.getAttribute('data-click-registered') === 'true')
      return
    widgetWrapperEl.value.addEventListener('click', handleDelegatedClick, true)
    widgetWrapperEl.value.setAttribute('data-click-registered', 'true')
  }

  const postRenderCallbacks = () => {
    if (!widgetWrapperEl.value) return

    if (widgetWrapperEl.value.getAttribute('data-render-registered') === 'true')
      return

    widgetWrapperEl.value.setAttribute('data-render-registered', 'true')
    track('NtpAd_Rendered', getEventData())
  }

  const executed = ref<boolean>(false)
  const handleTaboolaRenderedEvent = () => {
    if (!executed.value) {
      if (widgetWrapperEl.value) {
        setAdBrand(widgetWrapperEl.value)
      }

      attachClickTracking()

      fireRenderCallback(
        placementClassName.value,
        executed,
        postRenderCallbacks
      )
    }
  }

  const renderTaboolaAd = () => {
    renderTaboolaElement.value = true

    nextTick(() => {
      if (
        !widgetWrapperEl.value ||
        widgetWrapperEl.value?.getAttribute('data-render-registered')
      ) {
        return
      }
      const index = getIndexOfPlacement(props.placement, widgetWrapperEl.value)
      if (index > 0) {
        // for taboola properties endless starts at 4 since other units take <4
        const taboolaStartIndex = 4
        widgetName.value = widgetName.value + (index + taboolaStartIndex)
        containerName.value = props.containerName + (index + taboolaStartIndex)
        // class name we want the instance of the ad (not the same as taboola)
        placementClassName.value =
          index > 0 ? `${props.placement}-${index}` : props.placement
      }

      triggerTaboolaRenderedEvent(
        props.mode,
        containerName.value,
        placementClassName.value,
        handleTaboolaRenderedEvent
      )
    })
  }

  onMounted(() => {
    renderWhenCurrentElementIsVisible(
      widgetWrapperEl.value,
      props.lazyRender,
      props.sticky,
      renderTaboolaAd
    )
  })

  onUnmounted(() => {
    widgetWrapperEl.value?.removeEventListener(
      'click',
      handleDelegatedClick,
      true
    )
    executed.value = false
  })
</script>
<template>
  <div
    ref="widgetWrapperEl"
    :key="props.adInstanceId"
    :class="[props.placement, placementClassName, widgetName]"
    :data-ntp-extern-id="taboolaExternId"
    :data-ad-instance-id="props.adInstanceId"
  >
    <div
      v-if="renderTaboolaElement"
      :id="containerName"
      class="TABOOLA"
      :data-ad-brand="adBrand"
    />
  </div>
</template>
